import React, { useState, useEffect, useMemo } from 'react';
import style from './BookingSlotPicker.module.css';

const BookingConfirmationOverlay = ({
    isOpen,
    onClose,
    onConfirm,
    services = [], // New prop for services list
    data = {} // { date, time, professional }
}) => {
    // State
    const [name, setName] = useState('');
    const [selectedServiceIds, setSelectedServiceIds] = useState([]); // Changed to Array
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchVisible, setIsSearchVisible] = useState(false);

    // Validation State
    const [errors, setErrors] = useState({ name: '', service: '' });

    // Reset state when opening/closing
    useEffect(() => {
        if (isOpen) {
            setName('');
            setSelectedServiceIds([]);
            setErrors({ name: '', service: '' });
            setIsSubmitting(false);
            setSearchTerm('');
            setIsSearchVisible(false);
        }
    }, [isOpen]);

    const filteredServices = useMemo(() => {
        if (!searchTerm) {
            return services;
        }
        return services.filter(service =>
            service.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [services, searchTerm]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        // Validation
        const newErrors = {};
        let hasError = false;

        if (selectedServiceIds.length === 0) {
            newErrors.service = 'Por favor, selecione pelo menos um serviço.';
            hasError = true;
        }

        if (!name.trim()) {
            newErrors.name = 'Por favor, digite seu nome.';
            hasError = true;
        }

        if (hasError) {
            setErrors(newErrors);
            return;
        }

        // Processing
        setIsSubmitting(true);

        // Find all selected service objects
        const selectedServices = services.filter(s => selectedServiceIds.includes(s.id));

        // Pass everything back to parent
        // Note: passing 'services' (plural) instead of 'service'
        onConfirm({ ...data, customerName: name, services: selectedServices });

        // Note: isSubmitting stays true until unmount/close, which happens immediately in parent usually
    };

    // Clear error on change
    const handleNameChange = (e) => {
        setName(e.target.value);
        if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
    };

    const toggleService = (serviceId) => {
        setSelectedServiceIds(prev => {
            const isSelected = prev.includes(serviceId);
            let newSelection;
            if (isSelected) {
                newSelection = prev.filter(id => id !== serviceId);
            } else {
                newSelection = [...prev, serviceId];
            }

            if (newSelection.length > 0 && errors.service) {
                setErrors(err => ({ ...err, service: '' }));
            }
            return newSelection;
        });
    };

    return (
        <div className={style.overlayBackdrop} onClick={onClose}>
            <div
                className={style.overlayCard}
                onClick={(e) => e.stopPropagation()} // Prevent close on card click
            >
                <div className={style.overlayHeader}>
                    <div className={style.subtitle}>Confirmar Agendamento</div>
                    <h2 className={style.confirmTitle}>{data.time || '--:--'}</h2>
                    <div className={style.subtitle} style={{ marginTop: '0.5rem' }}>
                        {data.date ? new Date(data.date).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Data não selecionada'}
                    </div>
                </div>

                <div className={style.infoRow}>
                    <div className={style.infoItem}>
                        <span>Profissional:</span>
                        <span className={style.infoValue}>{data.professional?.fullname || 'N/A'}</span>
                    </div>
                </div>

                <div className={style.inputGroup}>
                    {/* Service Selection */}
                    <div className={style.inputWrapper}>
                        <div className={style.serviceSearchHeader}>
                            <label className={style.inputLabel} style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', marginBottom: '8px', display: 'block' }}>
                                Selecione o(s) Serviço(s)
                            </label>
                            <button className={style.searchIconBtn} onClick={() => setIsSearchVisible(!isSearchVisible)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                            </button>
                        </div>
                        {isSearchVisible && (
                            <input
                                type="text"
                                placeholder="Pesquisar serviço..."
                                className={style.searchInput}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        )}

                        <div className={style.serviceList}>
                            {filteredServices.length > 0 ? filteredServices.map(service => {
                                const isSelected = selectedServiceIds.includes(service.id);
                                return (
                                    <div
                                        key={service.id}
                                        className={`${style.serviceCard} ${isSelected ? style.selected : ''}`}
                                        onClick={() => toggleService(service.id)}
                                    >
                                        <div className={style.serviceInfo}>
                                            <span className={style.serviceName} title={service.name}>{service.name}</span>
                                            <div className={style.serviceMeta}>
                                                <span className={style.serviceDuration}>
                                                    ⏱ {service.time_service ? service.time_service.slice(0, 5) : '--:--'}
                                                </span>
                                                <span className={style.servicePrice}>
                                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(service.price)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className={style.serviceCheck}>
                                            {isSelected && (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div style={{ color: 'rgba(255,255,255,0.5)', padding: '1rem', textAlign: 'center' }}>
                                    Nenhum serviço disponível.
                                </div>
                            )}
                        </div>

                        {errors.service && <div className={style.errorMessage}>{errors.service}</div>}
                    </div>

                    <div className={style.inputWrapper}>

                        <input
                            type="text"
                            placeholder="Seu Nome"
                            className={`${style.inputField} ${errors.name ? style.error : ''}`}
                            value={name}
                            onChange={handleNameChange}
                            onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                            disabled={isSubmitting}
                        />
                        {errors.name && <div className={style.errorMessage}>{errors.name}</div>}
                    </div>
                </div>

                <button
                    className={style.confirmBtn}
                    onClick={handleConfirm}
                    disabled={isSubmitting}
                    style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'wait' : 'pointer' }}
                >
                    {!isSubmitting && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.865-.79-1.463-1.764-1.638-2.062-.174-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                    )}
                    {isSubmitting ? 'Processando...' : 'Confirmar no WhatsApp'}
                </button>

                <button
                    className={style.cancelBtn}
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
            </div>
        </div>
    );
};

export default BookingConfirmationOverlay;
